import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, Select, Tag, Space, Popconfirm, message, Drawer } from 'antd';
import http from '../api/http';

const riskColors = { '低':'green', '中':'blue', '高':'orange', '极高':'red' };

const BehaviorTagsPage = () => {
  const [loading, setLoading] = useState(false);
  const [tags, setTags] = useState([]);
  const [visible, setVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [assignVisible, setAssignVisible] = useState(false);
  const [assigningTag, setAssigningTag] = useState(null);
  const [assignEmployees, setAssignEmployees] = useState([]); // 已分配员工列表
  const [allEmployees, setAllEmployees] = useState([]); // 可选员工
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [assignLoading, setAssignLoading] = useState(false);
  const [query, setQuery] = useState({ q:'', category:'', risk_level:'', status:'active' });

  const loadTags = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(query).forEach(([k,v])=>{ if(v) params[k]=v; });
      const { data } = await http.get('/behavior/tags', { params });
      setTags(Array.isArray(data)? data : []);
    } catch(e){ console.error(e); message.error(e.message || '标签加载失败'); }
    setLoading(false);
  };

  useEffect(()=>{ loadTags(); }, [query]);

  const openAdd = () => { setEditing(null); form.resetFields(); setVisible(true); };
  const openEdit = (record) => { setEditing(record); form.setFieldsValue({ ...record }); setVisible(true); };

  const submit = async () => {
    try { await form.validateFields(); } catch { return; }
    const v = form.getFieldsValue();
    try {
      if(editing){
        await http.put(`/behavior/tags/${editing.id}`, v);
        message.success('更新成功');
      } else {
        await http.post('/behavior/tags', v);
        message.success('创建成功');
      }
      setVisible(false); loadTags();
    } catch(e){ message.error(e.message || (editing? '更新失败':'创建失败')); }
  };

  const removeTag = async (id) => {
    try {
      await http.delete(`/behavior/tags/${id}`);
      message.success('删除成功');
      loadTags();
    } catch(e){ message.error(e.message || '删除失败'); }
  };

  const openAssign = async (record) => {
    setAssigningTag(record);
    setAssignVisible(true);
    setAssignLoading(true);
    try {
      // 获取已分配员工
      const { data: assigned } = await http.get(`/behavior/tags/${record.id}/assignments`);
      setAssignEmployees(Array.isArray(assigned)? assigned: []);
      // 获取员工列表（简单查询）
      const { data: employees } = await http.get('/employees');
      setAllEmployees(Array.isArray(employees)? employees: []);
      setSelectedEmployeeIds([]);
    } catch(e){ message.error(e.message || '加载分配数据失败'); }
    setAssignLoading(false);
  };

  const submitAssign = async () => {
    if(!assigningTag) return;
    if(!selectedEmployeeIds.length){ message.warning('请选择至少一名员工'); return; }
    setAssignLoading(true);
    try {
      await http.post(`/behavior/tags/${assigningTag.id}/assignments`, { employee_ids: selectedEmployeeIds });
      message.success('分配成功');
      // 重新加载已分配列表
      const { data: assigned } = await http.get(`/behavior/tags/${assigningTag.id}/assignments`);
      setAssignEmployees(Array.isArray(assigned)? assigned: []);
      setSelectedEmployeeIds([]);
    } catch(e){ message.error(e.message || '分配失败'); }
    setAssignLoading(false);
  };

  const removeAssignment = async (empId) => {
    if(!assigningTag) return;
    setAssignLoading(true);
    try {
      await http.delete(`/behavior/tags/${assigningTag.id}/assignments/${empId}`);
      const { data: assigned } = await http.get(`/behavior/tags/${assigningTag.id}/assignments`);
      setAssignEmployees(Array.isArray(assigned)? assigned: []);
      message.success('移除成功');
    } catch(e){ message.error(e.message || '移除失败'); }
    setAssignLoading(false);
  };

  const columns = [
    { title:'分类', dataIndex:'category', width:120 },
    { title:'名称', dataIndex:'name', width:160 },
    { title:'风险等级', dataIndex:'risk_level', width:100, render:(v)=> <Tag color={riskColors[v]||'default'}>{v}</Tag> },
    { title:'颜色', dataIndex:'color', width:80, render:(c)=> c? <span style={{ display:'inline-block', width:20, height:20, background:c, border:'1px solid #ccc' }} /> : <span style={{color:'#999'}}>默认</span> },
    { title:'权重', dataIndex:'weight', width:80 },
    { title:'状态', dataIndex:'status', width:90, render:(v)=> v==='active'? <Tag color='green'>启用</Tag>:<Tag color='gray'>停用</Tag> },
    { title:'描述', dataIndex:'description', ellipsis:true },
    { title:'操作', width:220, render:(_,record)=> <Space>
        <Button size='small' onClick={()=>openEdit(record)}>编辑</Button>
        <Button size='small' onClick={()=>openAssign(record)}>分配</Button>
        <Popconfirm title='确认删除?' onConfirm={()=>removeTag(record.id)}>
          <Button size='small' danger>删除</Button>
        </Popconfirm>
      </Space> }
  ];

  return (
    <div>
      <h2 style={{ marginBottom:16 }}>人员行为标签体系</h2>
      <Space wrap style={{ marginBottom:16 }}>
        <Input placeholder='搜索名称/描述' allowClear style={{ width:200 }} value={query.q} onChange={e=> setQuery(q=>({...q,q:e.target.value}))} />
        <Select placeholder='分类' allowClear style={{ width:140 }} value={query.category||undefined} onChange={v=> setQuery(q=>({...q,category:v||''}))} options={[...new Set(tags.map(t=>t.category))].map(c=>({label:c,value:c}))} />
        <Select placeholder='风险等级' allowClear style={{ width:120 }} value={query.risk_level||undefined} onChange={v=> setQuery(q=>({...q,risk_level:v||''}))} options={['低','中','高','极高'].map(r=>({label:r,value:r}))} />
        <Select placeholder='状态' style={{ width:110 }} value={query.status} onChange={v=> setQuery(q=>({...q,status:v}))} options={[{label:'启用',value:'active'},{label:'停用',value:'inactive'},{label:'全部',value:''}]} />
        <Button type='primary' onClick={openAdd}>新增标签</Button>
        <Button onClick={loadTags}>刷新</Button>
      </Space>
      <Table size='small' rowKey='id' loading={loading} dataSource={tags} columns={columns} pagination={{ pageSize: 20 }} />
      <Modal title={editing? '编辑标签':'新增标签'} open={visible} onCancel={()=>setVisible(false)} onOk={submit} destroyOnClose>
        <Form layout='vertical' form={form}>
          <Form.Item name='category' label='分类' rules={[{required:true,message:'必填'}]}><Input /></Form.Item>
          <Form.Item name='name' label='名称' rules={[{required:true,message:'必填'}]}><Input /></Form.Item>
          <Form.Item name='description' label='描述'><Input.TextArea rows={3} /></Form.Item>
          <Form.Item name='risk_level' label='风险等级' initialValue='中'>
            <Select options={['低','中','高','极高'].map(r=>({label:r,value:r}))} />
          </Form.Item>
            <Form.Item name='weight' label='权重' initialValue={1} rules={[{validator(_,v){ if(v===undefined||v===''){return Promise.resolve();} if(Number(v)<=0) return Promise.reject('需>0'); return Promise.resolve();}}]}>
            <Input type='number' min={0.1} step={0.1} />
          </Form.Item>
          <Form.Item name='color' label='标签颜色(可选)'>
            <Input placeholder='#RRGGBB 或 留空' maxLength={16} />
          </Form.Item>
          <Form.Item name='status' label='状态' initialValue='active'>
            <Select options={[{label:'启用',value:'active'},{label:'停用',value:'inactive'}]} />
          </Form.Item>
        </Form>
      </Modal>
        <Drawer title={assigningTag? `标签分配: ${assigningTag.name}`:'标签分配'} open={assignVisible} onClose={()=>{setAssignVisible(false); setAssigningTag(null);}} width={520}>
          <Space direction='vertical' style={{ width:'100%' }} size='middle'>
            <Select
              mode='multiple'
              allowClear
              placeholder='选择要分配的员工'
              value={selectedEmployeeIds}
              onChange={setSelectedEmployeeIds}
              loading={assignLoading}
              style={{ width:'100%' }}
              options={allEmployees.map(e=>({ label: `${e.name}${e.department?`(${e.department})`:''}`, value: e.id }))}
              showSearch
              optionFilterProp='label'
            />
            <Button type='primary' onClick={submitAssign} loading={assignLoading}>批量分配</Button>
            <Table
              size='small'
              rowKey='employee_id'
              loading={assignLoading}
              dataSource={assignEmployees}
              columns={[
                { title:'员工', dataIndex:'employee_name', width:140 },
                { title:'部门', dataIndex:'department', width:120 },
                { title:'分配时间', dataIndex:'assigned_at', width:160 },
                { title:'操作', width:80, render:(_,r)=> <Button danger size='small' onClick={()=>removeAssignment(r.employee_id)}>移除</Button> }
              ]}
              pagination={false}
              style={{ maxHeight:320, overflow:'auto' }}
            />
          </Space>
        </Drawer>
    </div>
  );
};

export default BehaviorTagsPage;
